import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Coins, CheckCircle2, Loader2, Wallet, AlertTriangle, Star, History, RefreshCw, Sparkles, ArrowRight, Gift, Info, Pencil, ExternalLink, BadgeCheck, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import EVCStats from './EVCStats';

import SafeHtml from './SafeHtml';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EVCDashboard — customer-facing page to buy WinOLS credits.
 *  - Step 1: link an EVC customer ID to the wallet account (one time).
 *  - Step 2: pick one of the admin-defined packages, confirm, charge wallet,
 *            top-up EVC account, refresh both balances.
 *  - Always visible: wallet balance + EVC balance side-by-side, plus a
 *    history table of the customer's previous EVC purchases.
 */
export default function EVCDashboard({ token, onChargedWallet, onNavigate, refreshEvcBalance }) {
  const { t, i18n } = useTranslation('evc');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Localisation policy for admin-managed copy (intro_text, link_text,
  // confirmation_link_text): on English, prefer admin's value so operators
  // can customise. On any other locale, prefer the shipped translation so
  // non-EN customers don't see English; admin's value is only used as a
  // last-resort fallback when the locale key is missing.
  const lang = (i18n.language || 'en').toLowerCase();
  const isEnglish = lang === 'en' || lang.startsWith('en-');
  const pickLocalisedCopy = (adminVal, key, fallback) => {
    const admin = (adminVal || '').trim();
    const tr = t(key, { defaultValue: '' }) || '';
    if (isEnglish) return admin || tr || fallback;
    return tr || admin || fallback;
  };

  const [status, setStatus] = useState(null);   // { evc_customer_id, evc_balance, intro_text, currency, wallet_balance }
  const [packages, setPackages] = useState([]);
  const [bonusPackages, setBonusPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eidInput, setEidInput] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkErr, setLinkErr] = useState('');
  const [purchasing, setPurchasing] = useState(null); // package id while busy
  const [history, setHistory] = useState([]);
  const [confirmPkg, setConfirmPkg] = useState(null);
  // When the customer is already linked but wants to change their EVC ID,
  // we open the same form with the current value pre-filled.
  const [editingId, setEditingId] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, h, b] = await Promise.all([
        fetch(`${API_URL}/api/evc/customer/me`, { headers }).then((r) => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/evc/packages?active_only=true`, { headers }).then((r) => r.ok ? r.json() : { items: [] }),
        fetch(`${API_URL}/api/evc/customer/me/purchases`, { headers }).then((r) => r.ok ? r.json() : { items: [] }),
        fetch(`${API_URL}/api/config/bonus-packages`).then((r) => r.ok ? r.json() : []),
      ]);
      setStatus(s);
      setPackages(p.items || []);
      setHistory(h.items || []);
      setBonusPackages(Array.isArray(b) ? b.filter((x) => x.enabled !== false) : []);
      if (s?.evc_customer_id) setEidInput(String(s.evc_customer_id));
    } finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Show a one-time toast for users whose EVC ID was flagged as
  // "needs verification" at registration (EVC API rejected the value).
  // Clicking the toast opens the inline edit form so they can fix it.
  const warnedRef = React.useRef(false);
  useEffect(() => {
    if (!status || warnedRef.current) return;
    if (status.evc_needs_verification && status.evc_customer_id) {
      warnedRef.current = true;
      toast.warning(t('toasts.needs_verification_title', { defaultValue: 'Your EVC ID needs verification' }), {
        description: t('toasts.needs_verification_desc', { id: status.evc_customer_id, defaultValue: `EVC.de did not recognise customer #${status.evc_customer_id}. Please double-check and re-save.` }),
        duration: 8000,
        action: {
          label: t('toasts.fix_now', { defaultValue: 'Fix now' }),
          onClick: () => { setEidInput(String(status.evc_customer_id || '')); setLinkErr(''); setEditingId(true); },
        },
      });
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkEvc = async () => {
    setLinking(true); setLinkErr('');
    try {
      const r = await fetch(`${API_URL}/api/evc/customer/me`, {
        method: 'PUT', headers,
        body: JSON.stringify({ evc_customer_id: parseInt(eidInput, 10) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setLinkErr(j.detail || `HTTP ${r.status}`); return; }
      await load();
      setEditingId(false);
      if (refreshEvcBalance) refreshEvcBalance();
      toast.success(t('toasts.linked_title', { defaultValue: 'EVC ID linked' }), {
        description: t('toasts.linked_desc', { id: j.evc_customer_id, bal: j.evc_balance ?? '—', defaultValue: `Customer #${j.evc_customer_id} verified — balance ${j.evc_balance ?? '—'} credits.` }),
      });
    } catch (e) { setLinkErr(e.message); }
    finally { setLinking(false); }
  };

  const purchase = async (pkg) => {
    setConfirmPkg(null);
    setPurchasing(pkg.id);
    try {
      const r = await fetch(`${API_URL}/api/evc/purchase`, {
        method: 'POST', headers, body: JSON.stringify({ package_id: pkg.id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(t('toasts.purchase_failed', { defaultValue: 'Purchase failed' }), {
          description: j.detail || `Server returned HTTP ${r.status}.`,
          duration: 6000,
        });
        return;
      }
      // Refresh dashboard data + parent wallet/header EVC balance.
      await load();
      if (onChargedWallet) onChargedWallet();
      if (refreshEvcBalance) refreshEvcBalance();
      // Cross-component signal — any admin EVC analytics panel listening
      // for `evc-purchase-completed` will auto-refresh its sales history
      // so the new revenue row appears without a manual Refresh click.
      try { window.dispatchEvent(new CustomEvent('evc-purchase-completed', { detail: { package_id: pkg.id, credits: pkg.credits, price_eur: pkg.price_eur } })); } catch (_) { /* old browsers */ }
      toast.success(t('toasts.purchase_added', { credits: pkg.credits, defaultValue: `+${pkg.credits} EVC credits added` }), {
        description: t('toasts.purchase_added_desc', {
          label: pkg.label,
          price: `€${Number(pkg.price_eur).toFixed(2)}`,
          bal: j.balance_after ?? '—',
          defaultValue: `${pkg.label} — €${Number(pkg.price_eur).toFixed(2)} debited from your wallet. New EVC balance: ${j.balance_after ?? '—'} credits.`,
        }),
        duration: 6000,
      });
    } catch (e) {
      toast.error(t('toasts.purchase_failed', { defaultValue: 'Purchase failed' }), { description: e.message || t('toasts.purchase_network', { defaultValue: 'Network error.' }), duration: 6000 });
    } finally {
      setPurchasing(null);
    }
  };

  // Upsell hint: pick the smallest bonus pack that closes the shortfall to
  // the cheapest EVC pack. Falls back to the most "popular" bonus pack, then
  // to the largest available. Only relevant when at least one EVC pack is
  // currently unaffordable.
  const wallet = Number(status?.wallet_balance || 0);
  const upsellSuggestion = useMemo(() => {
    if (!packages.length || !bonusPackages.length) return null;
    const cheapest = packages.reduce((m, p) => (p.price_eur < m.price_eur ? p : m), packages[0]);
    const shortfall = cheapest.price_eur - wallet;
    if (shortfall <= 0) return null;
    const sorted = [...bonusPackages].sort((a, b) => (a.amount || 0) - (b.amount || 0));
    const fits = sorted.find((b) => (b.amount || 0) >= shortfall);
    const popular = bonusPackages.find((b) => b.popular);
    return fits || popular || sorted[sorted.length - 1] || null;
  }, [packages, bonusPackages, wallet]);

  if (loading) {
    return <div className="p-8 text-center text-slate-500" data-testid="evc-loading"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t('loading', { defaultValue: 'Loading EVC store…' })}</div>;
  }

  const linked = !!status?.evc_customer_id;
  const evcBal = status?.evc_balance;

  // Baseline = €/credit on the cheapest pack. Customer-facing "Save €X" badge.
  const baselinePerCredit = (() => {
    if (!packages.length) return null;
    let cheapest = packages[0];
    for (const p of packages) if (p.price_eur < cheapest.price_eur) cheapest = p;
    return Number(cheapest.price_eur) / Math.max(cheapest.credits, 1);
  })();
  const savingsFor = (p) => {
    if (!baselinePerCredit) return 0;
    const wouldCost = baselinePerCredit * p.credits;
    const s = wouldCost - Number(p.price_eur);
    return s > 0.5 ? s : 0;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="evc-dashboard">
      {/* Hero — compact: title + intro + collapsible help + balances */}
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white rounded-2xl shadow-xl p-4 sm:p-5" data-testid="evc-hero">
        <details className="group" open data-testid="evc-hero-details">
          <summary className="list-none cursor-pointer flex items-start gap-3" data-testid="evc-help-toggle">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shadow-inner flex-shrink-0">
              <Coins className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">{t('hero.title', { defaultValue: 'EVC Credits Store' })}</h1>
                {status?.help_text && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-xs font-medium transition border border-white/20 select-none">
                    <Info className="w-3.5 h-3.5" />
                    <span>{t('hero.how_it_works', { defaultValue: 'How it works' })}</span>
                    <span className="text-white/70 group-open:rotate-180 transition-transform inline-block">▾</span>
                  </span>
                )}
              </div>
              {(() => {
                const html = pickLocalisedCopy(status?.intro_text, 'hero_subtitle', '');
                if (!html) return null;
                return (
                  <SafeHtml as="div" className="mt-1.5 text-white/90 text-xs sm:text-sm leading-snug [&_a]:underline [&_b]:font-semibold"
                    data-testid="evc-intro" html={html} />
                );
              })()}
            </div>
          </summary>

          {(() => {
            const html = pickLocalisedCopy(status?.help_text, 'help_text', '');
            if (!html) return null;
            return (
              <>
                {/* Links inside the admin-written help text used to be plain
                    underlined words ("this link") that nobody noticed. They
                    are rendered as white pill buttons instead. */}
                <SafeHtml as="div" className="mt-3 rounded-lg bg-white/12 backdrop-blur border border-white/20 p-3 sm:p-4 text-xs sm:text-sm text-white/95 leading-relaxed [&_ul]:space-y-1.5 [&_ul]:list-none [&_ul]:p-0 [&_li]:leading-relaxed [&_b]:font-semibold [&_b]:whitespace-nowrap [&_a]:inline-flex [&_a]:items-center [&_a]:gap-1 [&_a]:px-2 [&_a]:py-0.5 [&_a]:mx-0.5 [&_a]:rounded-md [&_a]:bg-white [&_a]:text-orange-700 [&_a]:font-bold [&_a]:no-underline [&_a]:shadow-sm [&_a:hover]:bg-orange-50 [&_a]:transition-colors"
                  data-testid="evc-help-body" html={html} />

                {/* Primary reseller CTA — the single action most customers
                    need here, so it gets a real button instead of hiding as
                    a word inside a paragraph. */}
                {status?.confirmation_link_enabled !== false && status?.confirmation_link_url && (
                  <a
                    href={status.confirmation_link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="evc-reseller-cta"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-orange-700 font-bold text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all"
                  >
                    <BadgeCheck className="w-4 h-4" />
                    {t('hero.reseller_cta', { defaultValue: 'Request reseller confirmation (AR / AUTO-REMAP.COM)' })}
                    <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                  </a>
                )}
              </>
            );
          })()}
        </details>

        {/* Balance cards */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <div className="bg-white/15 backdrop-blur rounded-lg px-3.5 py-2.5 border border-white/20" data-testid="evc-wallet-card">
            <div className="flex items-center gap-1.5 text-white/80 text-[10px] uppercase tracking-wider"><Wallet className="w-3 h-3" /> {t('wallet_balance', { defaultValue: 'Wallet balance' })}</div>
            <div className="mt-0.5 text-2xl font-bold">€{wallet.toFixed(2)}</div>
          </div>
          <div className="bg-white/15 backdrop-blur rounded-lg px-3.5 py-2.5 border border-white/20" data-testid="evc-balance-card">
            <div className="flex items-center gap-1.5 text-white/80 text-[10px] uppercase tracking-wider"><Coins className="w-3 h-3" /> {t('evc_balance', { defaultValue: 'EVC balance' })}</div>
            <div className="mt-0.5 text-2xl font-bold">
              {linked ? (evcBal !== null && evcBal !== undefined ? `${evcBal} ${t('credits', { defaultValue: 'credits' })}` : '—') : t('not_linked', { defaultValue: 'Not linked' })}
            </div>
            {linked && (
              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap rounded-lg bg-white/15 border border-white/25 px-2.5 py-1.5">
                <span className="inline-flex items-baseline gap-1.5" data-testid="evc-id-label">
                  <Hash className="w-4 h-4 opacity-70 self-center" />
                  <span className="text-lg font-extrabold tracking-wide tabular-nums">{status.evc_customer_id}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">
                    {t('evc_id_short', { defaultValue: 'EVC ID' })}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => { setEidInput(String(status.evc_customer_id || '')); setLinkErr(''); setEditingId((v) => !v); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-orange-700 text-xs font-bold shadow-sm hover:bg-orange-50 active:scale-[0.98] transition"
                  data-testid="evc-id-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {editingId
                    ? t('cancel', { defaultValue: 'Cancel' })
                    : t('change_id', { defaultValue: 'Change ID' })}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Link section — shown until linked, or when the customer clicks
          "Edit" on the hero balance card to change their EVC ID. */}
      {(!linked || editingId) && (
        <section className="bg-white border border-amber-200 rounded-xl shadow-sm px-4 py-3" data-testid="evc-link-section">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800">{editingId ? t('change_evc_id_title', { defaultValue: 'Change your EVC customer ID' }) : t('link_evc_id_title', { defaultValue: 'Link your EVC customer ID' })}</div>
                <div className="text-xs text-slate-500 leading-snug" data-testid="evc-link-prompt">
                  {pickLocalisedCopy(status?.link_text, 'link_prompt_default', "Enter the WinOLS customer ID — we'll verify and connect it so credits deliver automatically.")}
                </div>
                {/* Secondary CTA — admin-configurable phrase + EVC
                    reseller confirmation URL. Wraps cleanly on
                    mobile, opens in a new tab. Hidden when admin
                    disables the toggle in EVC settings. */}
                {status?.confirmation_link_enabled !== false && status?.confirmation_link_url && (
                  <div
                    className="mt-2 text-xs text-slate-500 leading-snug"
                    data-testid="evc-confirmation-link"
                  >
                    {pickLocalisedCopy(status?.confirmation_link_text, 'confirmation_link_default', 'Also additionally you can use this link to request confirmation:')}
                    {' '}
                    <a
                      href={status.confirmation_link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1.5 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-800 font-bold hover:bg-cyan-100 transition-colors"
                      data-testid="evc-confirmation-link-anchor"
                    >
                      <BadgeCheck className="w-3.5 h-3.5" />
                      {t('request_confirmation', { defaultValue: 'Request reseller confirmation' })}
                      <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-70" />
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:flex-shrink-0 sm:flex-nowrap items-stretch">
              <input
                type="number"
                inputMode="numeric"
                placeholder={t('placeholder_id', { defaultValue: 'e.g. 33333' })}
                value={eidInput}
                onChange={(e) => setEidInput(e.target.value)}
                className="flex-1 min-w-[96px] sm:flex-none sm:w-36 border-2 border-amber-300 rounded-lg px-3 py-2.5 text-base font-mono font-bold tracking-wide text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                data-testid="evc-id-input"
              />
              <button
                onClick={linkEvc}
                disabled={!eidInput || linking}
                className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 whitespace-nowrap shadow-sm transition-colors"
                data-testid="evc-link-btn"
              >
                {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {linking ? t('verifying', { defaultValue: 'Verifying…' }) : (editingId ? t('save_verify', { defaultValue: 'Save & verify' }) : t('verify_link', { defaultValue: 'Verify & link' }))}
              </button>
              {editingId && (
                <button
                  onClick={() => { setEditingId(false); setLinkErr(''); setEidInput(String(status?.evc_customer_id || '')); }}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
                  data-testid="evc-edit-cancel"
                >{t('cancel', { defaultValue: 'Cancel' })}</button>
              )}
            </div>
          </div>
          {linkErr && <div className="mt-2 text-xs text-rose-600" data-testid="evc-link-error">{linkErr}</div>}
        </section>
      )}

      {/* Smart upsell — only shown when wallet < cheapest EVC package */}
      {linked && upsellSuggestion && (
        <section
          className="relative overflow-hidden rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 shadow-sm p-5 sm:p-6"
          data-testid="evc-upsell-card"
        >
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-emerald-300/20 rounded-full blur-2xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-800 text-base sm:text-lg">{t('upsell.title', { defaultValue: 'Need more wallet credit?' })}</h3>
                {upsellSuggestion.popular && (
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                    <Star className="w-2.5 h-2.5" /> {t('upsell.popular_badge', { defaultValue: 'POPULAR' })}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-600">
                <Trans
                  i18nKey="upsell.top_up_text"
                  ns="evc"
                  values={{
                    amount: Number(upsellSuggestion.amount).toFixed(0),
                    bonus: Number(upsellSuggestion.bonus).toFixed(0),
                    label: upsellSuggestion.label || 'Pack',
                    total: (Number(upsellSuggestion.amount) + Number(upsellSuggestion.bonus)).toFixed(0),
                  }}
                  components={{ b: <b />, em: <b className="text-emerald-700" /> }}
                  defaults="Top up <b>€{{amount}}</b> and get a <b>+€{{bonus}} bonus</b> ({{label}}). That's <b>€{{total}}</b> ready to spend on EVC credits."
                />
              </p>
            </div>
            <button
              onClick={() => onNavigate && onNavigate('recharge')}
              className="flex-shrink-0 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition flex items-center gap-2"
              data-testid="evc-upsell-cta"
            >
              <Gift className="w-4 h-4" /> {t('upsell.cta', { defaultValue: 'Top up & save' })}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {/* Packages */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6" data-testid="evc-packages">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-800">{t('packages.choose', { defaultValue: 'Choose a credit package' })}</h2>
          <button onClick={load} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="evc-reload">
            <RefreshCw className="w-3 h-3" /> {t('packages.refresh', { defaultValue: 'Refresh' })}
          </button>
        </div>
        {packages.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">{t('packages.none_available', { defaultValue: 'No packages available right now. Please contact support.' })}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {packages.map((p) => {
              const insufficient = wallet < p.price_eur;
              const busy = purchasing === p.id;
              const savings = savingsFor(p);
              return (
                <div
                  key={p.id}
                  className={`relative rounded-xl border p-5 transition ${p.popular ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 shadow-md' : 'border-slate-200 bg-white hover:shadow-md'}`}
                  data-testid={`evc-pkg-${p.id}`}
                >
                  {p.popular && (
                    <div className="absolute -top-2 left-4 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1 shadow">
                      <Star className="w-3 h-3" /> {t('packages.popular', { defaultValue: 'POPULAR' })}
                    </div>
                  )}
                  {savings > 0 && (
                    <div className="absolute -top-2 right-4 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1 shadow" data-testid={`evc-pkg-save-${p.id}`}>
                      <Sparkles className="w-3 h-3" /> {t('packages.save_badge', { amount: savings.toFixed(0), defaultValue: `SAVE €${savings.toFixed(0)}` })}
                    </div>
                  )}
                  <div className="text-sm text-slate-600">{p.label}</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-slate-800">{p.credits}</span>
                    <span className="text-slate-500 text-sm">{t('credits', { defaultValue: 'credits' })}</span>
                  </div>
                  <div className="mt-1 text-xl font-semibold text-amber-700">€{Number(p.price_eur).toFixed(2)}</div>
                  <div className="text-[11px] text-slate-400">{t('packages.per_credit', { amount: `€${(Number(p.price_eur) / Math.max(p.credits, 1)).toFixed(2)}`, defaultValue: `€${(Number(p.price_eur) / Math.max(p.credits, 1)).toFixed(2)} / credit` })}</div>
                  {p.description && <div className="mt-2 text-xs text-slate-500">{p.description}</div>}
                  <button
                    onClick={() => setConfirmPkg(p)}
                    disabled={!linked || insufficient || busy}
                    className="mt-4 w-full px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-medium hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`evc-buy-btn-${p.id}`}
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                    {busy
                      ? t('packages.processing', { defaultValue: 'Processing…' })
                      : insufficient
                        ? t('packages.insufficient_balance', { defaultValue: 'Insufficient balance' })
                        : !linked
                          ? t('packages.link_first', { defaultValue: 'Link EVC ID first' })
                          : t('packages.buy_credits', { defaultValue: 'Buy EVC credits' })}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Customer-side stats — only renders when there is purchase history */}
      {linked && <EVCStats token={token} />}

      {/* History */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6" data-testid="evc-history">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
          <History className="w-4 h-4" /> {t('history.title', { defaultValue: 'Your purchases' })}
        </h2>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">{t('history.empty', { defaultValue: 'No purchases yet.' })}</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto -mx-2">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="text-left px-2.5 py-2 whitespace-nowrap">{t('history.date', { defaultValue: 'Date' })}</th>
                    <th className="text-left px-2.5 py-2 whitespace-nowrap">{t('history.package', { defaultValue: 'Package' })}</th>
                    <th className="text-right px-2.5 py-2 whitespace-nowrap">{t('history.credits', { defaultValue: 'Credits' })}</th>
                    <th className="text-right px-2.5 py-2 whitespace-nowrap">{t('history.spent', { defaultValue: 'Spent' })}</th>
                    <th className="text-right px-2.5 py-2 whitespace-nowrap" title="Live EVC balance recorded at the moment of this top-up. Real-time balance is shown on the header card above.">{t('history.evc_bal_snapshot', { defaultValue: 'EVC bal. snapshot' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100" data-testid={`evc-history-row-${p.id}`}>
                      <td className="px-2.5 py-2 text-slate-600 whitespace-nowrap">{p.created_at?.slice(0, 19).replace('T', ' ')}</td>
                      <td className="px-2.5 py-2 max-w-[260px] truncate" title={p.package_label}>{p.package_label}</td>
                      <td className="px-2.5 py-2 text-right font-semibold whitespace-nowrap tabular-nums">{p.credits}</td>
                      <td className="px-2.5 py-2 text-right text-rose-600 whitespace-nowrap tabular-nums">−€{Number(p.price_eur).toFixed(2)}</td>
                      <td className="px-2.5 py-2 text-right text-emerald-700 whitespace-nowrap tabular-nums">{p.balance_after ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden space-y-2" data-testid="evc-history-mobile">
              {history.map((p) => (
                <div key={`m-${p.id}`} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`evc-history-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800 truncate">{p.package_label}</div>
                      <div className="text-[11px] text-slate-500">{p.created_at?.slice(0, 19).replace('T', ' ')}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-base font-bold text-amber-700">+{p.credits}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t('credits', { defaultValue: 'credits' })}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-rose-600 font-medium">{t('history.from_wallet', { amount: `€${Number(p.price_eur).toFixed(2)}`, defaultValue: `−€${Number(p.price_eur).toFixed(2)} from wallet` })}</span>
                    <span className="text-emerald-700">
                      <Trans
                        i18nKey="history.evc_bal"
                        ns="evc"
                        values={{ n: p.balance_after ?? '—' }}
                        components={{ b: <b /> }}
                        defaults="EVC bal: <b>{{n}}</b>"
                      />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Confirm modal */}
      {confirmPkg && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="evc-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-semibold text-slate-800">{t('confirm.title', { defaultValue: 'Confirm EVC purchase' })}</h3>
            <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm">
              <Row label={t('confirm.package', { defaultValue: 'Package' })} value={confirmPkg.label} />
              <Row label={t('confirm.credits', { defaultValue: 'Credits' })} value={`${confirmPkg.credits} ${t('credits', { defaultValue: 'credits' })}`} />
              <Row label={t('confirm.price', { defaultValue: 'Price' })} value={`€${Number(confirmPkg.price_eur).toFixed(2)}`} highlight />
              <Row label={t('confirm.wallet_now', { defaultValue: 'Wallet now' })} value={`€${wallet.toFixed(2)}`} />
              <Row label={t('confirm.wallet_after', { defaultValue: 'Wallet after' })} value={`€${(wallet - confirmPkg.price_eur).toFixed(2)}`} />
              <Row label={t('confirm.evc_id_label', { defaultValue: 'EVC ID' })} value={`#${status?.evc_customer_id}`} />
              <Row label={t('confirm.evc_balance_now', { defaultValue: 'EVC balance now' })} value={evcBal === null || evcBal === undefined ? '—' : `${evcBal} ${t('credits', { defaultValue: 'credits' })}`} />
              <Row label={t('confirm.evc_balance_after', { defaultValue: 'EVC balance after' })} value={evcBal === null || evcBal === undefined ? `+${confirmPkg.credits} ${t('credits', { defaultValue: 'credits' })}` : `${evcBal + confirmPkg.credits} ${t('credits', { defaultValue: 'credits' })}`} highlight />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmPkg(null)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm" data-testid="evc-confirm-cancel">{t('confirm.cancel', { defaultValue: 'Cancel' })}</button>
              <button
                onClick={() => purchase(confirmPkg)}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg text-sm font-medium"
                data-testid="evc-confirm-buy"
              >
                {t('confirm.confirm_buy', { defaultValue: 'Confirm & Buy' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium ${highlight ? 'text-amber-700' : 'text-slate-800'}`}>{value}</span>
    </div>
  );
}
